import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const progressVariants = cva(
  'relative h-2 w-full overflow-hidden rounded-full',
  {
    variants: {
      variant: {
        default: 'bg-gray-200 dark:bg-gray-800',
        success: 'bg-success-100 dark:bg-success-900/30',
        error: 'bg-error-100 dark:bg-error-900/30',
        warning: 'bg-warning-100 dark:bg-warning-900/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const progressBarVariants = cva(
  'h-full transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'bg-brand-500',
        success: 'bg-success-500',
        error: 'bg-error-500',
        warning: 'bg-warning-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface ProgressProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressVariants> {
  value: number;
  max?: number;
  showLabel?: boolean;
  label?: string;
}

export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, variant, value, max = 100, showLabel, label, ...props }, ref) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

    return (
      <div ref={ref} className="w-full" {...props}>
        {(showLabel || label) && (
          <div className="mb-2 flex items-center justify-between text-sm">
            {label && <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>}
            {showLabel && (
              <span className="text-gray-600 dark:text-gray-400">{Math.round(percentage)}%</span>
            )}
          </div>
        )}
        <div className={cn(progressVariants({ variant }), className)}>
          <div
            className={progressBarVariants({ variant })}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={value}
            aria-valuemin={0}
            aria-valuemax={max}
          />
        </div>
      </div>
    );
  }
);

Progress.displayName = 'Progress';
